import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, TextField, Button, Paper, CircularProgress, Grid, FormControlLabel, Checkbox } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import { ShipTo } from '../../services/shipToApi';
import { fetchShipToById, createShipTo, updateShipTo, setSelectedItem, clearValidationErrors, setError, clearSuccessMessage } from '../../store/reducers/shipToSlice';

const useStyles = makeStyles((theme) => ({ root: { padding: theme.spacing(3) }, paper: { padding: theme.spacing(3), marginBottom: theme.spacing(3) }, formControl: { marginBottom: theme.spacing(2) }, buttonGroup: { marginTop: theme.spacing(3), display: 'flex', gap: theme.spacing(1) }, loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 } }));

const ShipToEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { selectedItem, loading, error, validationErrors, successMessage } = useSelector((s: RootState) => s.shipTo);

  const [formData, setFormData] = useState<Partial<ShipTo>>({ AttName: '', AttSt: '', AttApt: '', AttCity: '', AttState: '', AttZIP: '', AttPhone: '', AttEmail: '', Active: true });

  useEffect(() => {
    if (id) dispatch(fetchShipToById(parseInt(id))); else { dispatch(setSelectedItem(null)); setFormData({ AttName: '', AttSt: '', AttApt: '', AttCity: '', AttState: '', AttZIP: '', AttPhone: '', AttEmail: '', Active: true }); }
    dispatch(clearValidationErrors()); dispatch(setError(null)); dispatch(clearSuccessMessage());
  }, [id, dispatch]);

  useEffect(() => { if (selectedItem && id) setFormData({ ...selectedItem }); }, [selectedItem, id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target; const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (validationErrors.length) dispatch(clearValidationErrors()); if (error) dispatch(setError(null));
  };

  const handleSave = () => { const payload = { ...formData } as Omit<ShipTo, 'Id'>; if (id) dispatch(updateShipTo(parseInt(id), payload)); else dispatch(createShipTo(payload)); };
  const handleCancel = () => navigate('/catalogs/shipto');

  if (loading && (id && !selectedItem)) return (<Box className={classes.loadingContainer}><CircularProgress /></Box>);

  return (
    <Box className={classes.root}>
      <Typography variant="h4" gutterBottom>{id ? 'Edit Ship-To' : 'Add New Ship-To'}</Typography>
      {error && (<Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>)}
      {validationErrors && validationErrors.length > 0 && (<Alert severity="error" style={{ marginBottom: 16 }}>{validationErrors.map((err, idx) => (<div key={idx}>{err}</div>))}</Alert>)}
      {successMessage && (<Alert severity="success" style={{ marginBottom: 16 }}>{successMessage}</Alert>)}
      <Paper className={classes.paper}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Name" name="AttName" value={formData.AttName || ''} onChange={handleInputChange} required className={classes.formControl} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel control={<Checkbox name="Active" checked={!!formData.Active} onChange={handleInputChange} color="primary" />} label="Active" className={classes.formControl} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Street" name="AttSt" value={formData.AttSt || ''} onChange={handleInputChange} required className={classes.formControl} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Apt" name="AttApt" value={formData.AttApt || ''} onChange={handleInputChange} className={classes.formControl} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="City" name="AttCity" value={formData.AttCity || ''} onChange={handleInputChange} required className={classes.formControl} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="State" name="AttState" value={formData.AttState || ''} onChange={handleInputChange} required className={classes.formControl} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="ZIP" name="AttZIP" value={formData.AttZIP || ''} onChange={handleInputChange} required className={classes.formControl} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Phone" name="AttPhone" value={formData.AttPhone || ''} onChange={handleInputChange} required className={classes.formControl} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Email" name="AttEmail" value={formData.AttEmail || ''} onChange={handleInputChange} required className={classes.formControl} />
          </Grid>
        </Grid>
        <Box className={classes.buttonGroup}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
          <Button variant="outlined" onClick={handleCancel} disabled={loading}>Cancel</Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ShipToEdit; 